import { Injectable } from '@angular/core';
import { AbstractFeatureChildQueryEntity } from '@y42/feature';
import { Y42FeatureType } from '@y42/json-schema';
import { isNotNullish } from '@y42/util/nullish';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';
import { VisualizationCanvasElementQuery } from '../canvas-element';
import type { VisualizationCanvasElementUIModel } from '../canvas-element/visualization-canvas-element.model';
import { VisualizationDashboardStoreQuery } from '../dashboard/visualization-dashboard.store-query';
import type { VisualizationTabState, VisualizationTabUIModel } from './visualization-tab.model';
import { VisualizationTabStore } from './visualization-tab.store';

@Injectable({ providedIn: 'root' })
export class VisualizationTabQuery extends AbstractFeatureChildQueryEntity<
  VisualizationTabState,
  VisualizationTabUIModel
> {
  constructor(
    protected readonly store: VisualizationTabStore,
    protected readonly parentQuery: VisualizationDashboardStoreQuery,
    private readonly canvasElementQuery: VisualizationCanvasElementQuery,
  ) {
    super({
      parentQuery,
      store,
      toUIModel: (storeModel) => {
        const canvasElements: VisualizationCanvasElementUIModel[] = (storeModel.$children || [])
          .map(($path) => this.canvasElementQuery.getUIEntity($path))
          .filter(isNotNullish);

        return {
          featureType: Y42FeatureType.VisualizationDashboard,
          ...storeModel,
          dashboard: this.parentQuery.getEntity(storeModel.$parent)!,
          children: canvasElements,
          canvasElements,
        };
      },
    });
  }

  readonly active$: Observable<VisualizationTabUIModel | undefined> = combineLatest([
    this.selectActive(),
    this.canvasElementQuery.elementsByPath$,
  ]).pipe(
    map(([active]) => active && this.toUIModel(active)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly activeItems$: Observable<VisualizationTabUIModel[]> = combineLatest([
    this.parentQuery.selectActive(),
    this.selectAll(),
    this.canvasElementQuery.elementsByPath$,
  ]).pipe(
    map(([activeParent]) =>
      activeParent ? activeParent.$children.map((child) => this.getUIEntity(child)).filter(isNotNullish) : [],
    ),
  );

  get isEditMode() {
    return this.store.getValue().ui.editMode;
  }

  readonly editMode$ = this.select((st) => st.ui.editMode);

  readonly sidebar$ = this.select((st) => st.ui.sidebar);
}
